import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, HeartHandshake, Building2, Users } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import CategoryContactForm from "../components/CategoryContactForm";
import { useLanguage } from "../contexts/LanguageContext";

const VolunteerPage = () => {
  const { t, lang } = useLanguage();
  const [tab, setTab] = useState("volunteer");

  useEffect(() => {
    document.title = lang === "en" ? "Volunteer / Partner – Fondation Cœur Solidaire" : "Bénévole / Partenaire – Fondation Cœur Solidaire";
  }, [lang]);

  const isVol = tab === "volunteer";
  const blocks = isVol ? t.volunteerPage.volunteer : t.volunteerPage.partner;

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="volunteer-page">
      <Seo
        title={t.seo.volunteer.title}
        description={t.seo.volunteer.description}
        path={lang === "en" ? "/volunteer" : "/benevole"}
        lang={lang}
      />
      <Navbar />

      <section className="pt-32 pb-16 md:pt-40 md:pb-20 bg-gradient-to-b from-[#002B49] to-[#001F35] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 80%, #C5A059 0%, transparent 50%)" }} />
        <div className="container-narrow relative">
          <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-[#E8C77E] mb-6 text-sm transition-colors">
            <ArrowLeft size={14} /> {t.volunteerPage.back}
          </Link>
          <span className="eyebrow text-[#E8C77E]">{t.volunteerPage.eyebrow}</span>
          <span className="block w-16 h-px bg-[#C5A059] mt-4 mb-6" />
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium leading-[1.1]" data-testid="volunteer-title">
            {t.volunteerPage.title}
          </h1>
          <p className="text-white/85 text-lg md:text-xl mt-6 max-w-2xl leading-relaxed">
            {t.volunteerPage.intro}
          </p>

          <div className="inline-flex bg-white/10 border border-white/15 rounded-full p-1 mt-8" data-testid="volunteer-tabs">
            <TabBtn active={isVol} onClick={() => setTab("volunteer")} testid="tab-volunteer">
              <HeartHandshake size={14} className="mr-2" />
              {t.volunteerPage.tabs.volunteer}
            </TabBtn>
            <TabBtn active={!isVol} onClick={() => setTab("partner")} testid="tab-partner">
              <Building2 size={14} className="mr-2" />
              {t.volunteerPage.tabs.partner}
            </TabBtn>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="container-narrow grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <h2 className="font-serif text-3xl text-[#002B49] font-semibold">{blocks.heading}</h2>
            <p className="text-[#334155] mt-3 leading-relaxed">{blocks.description}</p>

            <div className="mt-8 space-y-4">
              {blocks.items.map((it, i) => (
                <motion.div
                  key={it.title}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className="bg-white border border-black/5 rounded-xl p-5"
                  data-testid={`${tab}-item-${i}`}
                >
                  <div className="flex items-center gap-2 text-[#C5A059] text-xs font-medium uppercase tracking-wider mb-1">
                    <Users size={12} /> {it.tag}
                  </div>
                  <div className="font-serif text-lg text-[#002B49] font-semibold">{it.title}</div>
                  <p className="text-sm text-[#64748B] mt-1.5 leading-relaxed">{it.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <h2 className="font-serif text-3xl text-[#002B49] mb-2 font-semibold">{blocks.formTitle}</h2>
            <p className="text-[#64748B] mb-6">{blocks.formSubtitle}</p>
            <CategoryContactForm
              key={tab}
              category={isVol ? "volunteer" : "partner"}
              t={blocks.form}
              defaultSubject={blocks.defaultSubject}
              testid={`${tab}-form`}
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const TabBtn = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-all ${
      active ? "bg-[#C5A059] text-white" : "text-white/80 hover:text-white"
    }`}
    data-testid={testid}
  >
    {children}
  </button>
);

export default VolunteerPage;
